import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { isFileViewerRoute, usePageMetadataTitle } from "../../hooks/usePageMetadataTitle";
import { isPublicResourcePath } from "../components/publicResources/publicResourcePaths";

function splitCamelCase(str: string) {
    return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function toTitleCase(str: string) {
    return splitCamelCase(str)
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const DynamicTitle = () => {
    const { pathname } = useLocation();

    const metadataTitle = usePageMetadataTitle(pathname);
    useEffect(() => {
        // Public directory pages set their own search-friendly titles.
        if (isPublicResourcePath(pathname)) return;
        if (isFileViewerRoute(pathname)) {
            const unreadPrefix = document.title.match(/^\(\d+\)\s*/)?.[0] || "";
            document.title = `${unreadPrefix}${metadataTitle || "File viewer"} - Waivera`;
            return;
        }
        // SPECIAL CASE for login
        if (pathname === "/" || pathname === "/login") {
            document.title = "Login - Waivera";
            return;
        }

        if (pathname.startsWith("/profile/user")) { 
            document.title = "User Profile - Waivera"
            return;
        }

        const fullTitleOverrides: Record<string, string> = {
            "/settings/calendar-subscription": "Calendar subscription - Waivera",
            "/drive/employee-files": "Employee Files - Waivera",
            "/drive/caseFileDrive": "Participant Files - Waivera",
            "/drive/personal": "Participant Files - Waivera",
        };

        if (fullTitleOverrides[pathname]) {
            document.title = fullTitleOverrides[pathname];
            return;
        }

        const titleOverrides: Record<string, string> = {
            "/terms-of-service": "Terms of Service",
            "/privacy-policy": "Privacy Policy",
            "/legal/sign/tos": "Terms of Service",
            "/legal/sign/data-agreement": "Data Processing Agreement",
            "/legal/sign/security-compliance-terms": "Security & Compliance Terms",
            "/legal/sign/electronic-signature-consent": "Electronic Signature Consent",
        };

        const parts = pathname.split("/").filter(Boolean);

        // Convert URL segments
        let formattedParts = parts.map(toTitleCase);

        // SPECIAL CASE: Drive CaseFileDrive → remove duplicate "Drive"
        if (formattedParts[0] === "Drive" && formattedParts[1]?.endsWith("Drive")) {
            formattedParts[1] = formattedParts[1].replace(/Drive$/, "").trim();
        }

        // Join words
        const rawTitle = titleOverrides[pathname] ?? (formattedParts.length ? formattedParts.join(" ") : "Waivera");

        // Remove notification count like "(3)"
        const cleaned = rawTitle.replace(/^\(\d+\)\s*/, "");

        document.title = `${cleaned} - Waivera`;
    }, [pathname, metadataTitle]);

    return null;
};
